import {
  Theme,
  border,
  color,
  inputElementStyles,
  optionStyles,
  propertyColorStyles,
  radius,
  rem,
} from '@thiememeulenhoff/lit-sdk';
import { PropertyColor } from '@thiememeulenhoff/types';
import { css, unsafeCSS } from 'lit';
import { tagName } from '../../helpers';

export const tag = tagName('text-input');
export const theme = new Theme(tag);

const propertyColorCallback = (propertyColor: PropertyColor) => {
  const selector = unsafeCSS(`[color=${propertyColor}]`);

  return css`
    :host(${selector})
      :where(input):not([disabled]):where(:focus, :focus-visible) {
      border-color: ${color('blue', 300)};
    }
  `;
};

const propertyColorStyle = propertyColorStyles(propertyColorCallback);

export const styles = [
  optionStyles,
  css`
    :host {
      align-items: normal;
      display: flex;
      flex-direction: column;
      row-gap: ${rem(4)};
    }

    :where(.input-wrapper) {
      display: inline-block;
      inline-size: 100%;
      position: relative;
    }

    :where(label) {
      display: none;
      font-weight: bold;
    }

    :where(.show) {
      display: block;
    }

    :where(slot[name='error']) {
      color: ${color('red', 600)};
    }

    :where(slot[name='description']) {
      color: ${color('grey', 600)};
      font-size: ${rem(14)};
    }

    :where(input) {
      ${inputElementStyles};
      background-color: ${color('white')};
      block-size: auto;
      box-sizing: border-box;
      display: block;
      inline-size: 100%;
      line-height: 1.5;
    }

    :where(input.size-large) {
      padding: ${rem(14)};
    }

    :where(input.pill) {
      border-radius: ${radius('extra-large')};
    }

    :where(input)::placeholder {
      color: ${color('grey', 600)};
      font-size: ${rem(16)};
      opacity: 1;
    }

    :where(a-icon) {
      color: ${color('blue', 600)};
    }

    :where(.clickable) {
      cursor: pointer;
    }

    :where(.reset) {
      color: ${color('grey', 600)};
      cursor: pointer;
      display: none;
    }

    :where(.input-wrapper.has-content) {
      :where(.reset) {
        display: block;
      }
    }

    :where(.input-wrapper.with-icon) {
      :where(input) {
        padding-inline-end: 30px;
      }
    }

    :where(.input-wrapper.with-icon-and-reset) {
      :where(input) {
        padding-inline-end: 70px;
      }
      :where(.reset) {
        border-inline-end: 1px solid ${color('grey', 400)};
        inset-inline-end: ${rem(36)};
        padding-inline-end: ${rem(8)};
      }
    }

    :where(input.error) {
      border-color: ${color('red', 300)};
    }

    :host([disabled]) {
      :where(input) {
        background: ${color('neutral', 100)};
        border: ${border(1, 'neutral', 300)};
        color: ${color('neutral', 400)};
        cursor: not-allowed;
      }
      :where(input)::placeholder {
        color: ${color('neutral', 400)};
      }
      :where(a-icon) {
        color: ${color('neutral', 400)};
      }
    }

    .error {
      color: ${color('red', 600)};
    }

    .small {
      font-size: ${rem(12)};
    }
  `,
  propertyColorStyle,
];
